import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";

const basicSchema = Yup.object().shape({
  role_name: Yup.string().required("Role name is required"),
  role_description: Yup.string().required("Description is required"),
});

const Add = ({ showModal, hideModal, getRoles }) => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPermissions();
  }, []);

  const getPermissions = async () => {
    try {
      const response = await axios.get("/permissions");
      setPermissions(response.data);
      setFilteredPermissions(response.data); // Initialize filtered list
    } catch (err) {
      notifyError("Failed to load permissions");
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredPermissions(
      permissions.filter((perm) =>
        perm.perm_name.toLowerCase().includes(query)
      )
    );
  };

  const notifySuccess = (message) => {
    toast.success(message, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const notifyError = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const formik = useFormik({
    initialValues: {
      role_name: "",
      role_description: "",
      permissions: [],
    },
    validationSchema: basicSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("role_name", values.role_name);
        formData.append("role_description", values.role_description);
        values.permissions.forEach((permId) =>
          formData.append("permissions", permId)
        );

        await axios.post("/roles", formData);
        notifySuccess("Role added successfully!");
        getRoles();
        hideModal();
      } catch (error) {
        notifyError("Failed to add role");
      } finally {
        setLoading(false);
      }
    },
  });

  const handlePermissionToggle = (permissionId) => {
    const updatedPermissions = formik.values.permissions.includes(permissionId)
      ? formik.values.permissions.filter((id) => id !== permissionId)
      : [...formik.values.permissions, permissionId];

    formik.setFieldValue("permissions", updatedPermissions);
  };

  return (
    <>
      <Dialog open={showModal} onClose={hideModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Add Role
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Role Name Input */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role Name"
                  name="role_name"
                  variant="outlined"
                  value={formik.values.role_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.role_name && Boolean(formik.errors.role_name)
                  }
                  helperText={
                    formik.touched.role_name && formik.errors.role_name
                  }
                  required
                />
              </Grid>

              {/* Role Description Input */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="role_description"
                  variant="outlined"
                  value={formik.values.role_description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.role_description &&
                    Boolean(formik.errors.role_description)
                  }
                  helperText={
                    formik.touched.role_description &&
                    formik.errors.role_description
                  }
                  required
                />
              </Grid>

              {/* Permissions Search and List */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Allowed Permissions CCA
                </Typography>

                {/* Search Field */}
                <TextField
                  fullWidth
                  placeholder="Search permissions..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton>
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Scrollable Permission List */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  {filteredPermissions.map((perm) => (
                    <FormControlLabel
                      key={perm.id}
                      control={
                        <Checkbox
                          checked={formik.values.permissions.includes(perm.id)}
                          onChange={() => handlePermissionToggle(perm.id)}
                          color="primary"
                        />
                      }
                      label={perm.perm_name}
                    />
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={hideModal} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={formik.handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Add Role"}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </>
  );
};

export default Add;
